import path from 'node:path';

import type ProgressBar from '../console/progressBar.js';
import { ProgressBarSymbol } from '../console/progressBar.js';
import type DAT from '../models/dats/dat.js';
import Disk from '../models/dats/disk.js';
import type Game from '../models/dats/game.js';
import IgirHeader from '../models/dats/igirHeader.js';
import LogiqxDAT from '../models/dats/logiqx/logiqxDat.js';
import type Options from '../models/options.js';
import type WriteCandidate from '../models/writeCandidate.js';
import FsUtil from '../utils/fsUtil.js';
import Module from './module.js';

/**
 * Write a DAT that was generated by {@link DATGameInferrer} to disk.
 */
export default class Dir2DatCreator extends Module {
  private readonly options: Options;

  constructor(options: Options, progressBar: ProgressBar) {
    super(progressBar, Dir2DatCreator.name);
    this.options = options;
  }

  /**
   * Write the DAT.
   */
  async create(dat: DAT, candidates: WriteCandidate[]): Promise<string | undefined> {
    if (!this.options.shouldDir2Dat()) {
      return undefined;
    }

    if (candidates.length === 0) {
      this.prefixedLogger.trace(`${dat.getName()}: no candidates to create dir2dat for`);
      return undefined;
    }

    this.prefixedLogger.trace(`${dat.getName()}: writing dir2dat`);
    this.progressBar.setSymbol(ProgressBarSymbol.WRITING);
    this.progressBar.resetProgress(1);

    /**
     * It is possible that the {@link ROM} embedded within {@link WriteCandidate}s has been
     * manipulated, such as from {@link CandidateExtensionCorrector}. Use the {@link Game}s and
     * {@link ROM}s from the {@link WriteCandidate}s instead of the original {@link DAT}.
     */
    const gamesToCandidates = candidates.reduce((map, candidate) => {
      if (!candidate.canWrite()) {
        return map;
      }

      const key = candidate.getGame();
      if (map.has(key)) {
        map.get(key)?.push(candidate);
      } else {
        map.set(key, [candidate]);
      }
      return map;
    }, new Map<Game, WriteCandidate[]>());
    const gamesFromCandidates = Array.from(gamesToCandidates, ([game, candidates]) => {
      const roms = candidates
        .at(0)
        ?.getRomsWithFiles()
        .map((romWithFiles) => romWithFiles.getRom());
      return game.withProps({
        roms: roms?.filter((rom) => !(rom instanceof Disk)),
        disks: roms?.filter((rom) => rom instanceof Disk),
      });
    });

    const dir2datDir = this.options.getDir2DatOutput();
    if (!(await FsUtil.exists(dir2datDir))) {
      await FsUtil.mkdir(dir2datDir, { recursive: true });
    }

    // Construct a new DAT and write it to the output dir
    const header = new IgirHeader('dir2dat', dat, this.options);
    const dir2dat = new LogiqxDAT({ header, games: gamesFromCandidates });
    const dir2datContents = dir2dat.toXmlDat();
    const dir2datPath = path.join(dir2datDir, dir2dat.getFilename());
    this.prefixedLogger.info(`${dir2dat.getName()}: creating dir2dat '${dir2datPath}'`);
    await FsUtil.writeFile(dir2datPath, dir2datContents);

    this.prefixedLogger.trace(`${dir2dat.getName()}: done writing dir2dat`);
    return dir2datPath;
  }
}
